import React, { createContext, useContext, useReducer, useCallback, useEffect, ReactNode } from 'react';
import { 
  WorkflowType, 
  AnyWorkflowInstance, 
  WorkflowAction, 
  WorkflowState, 
  WorkflowContextType,
  StepStatus
} from '../types/workflow';
import { workflowService } from '../services/workflowService';
import { ValidationResult, ValidationError } from '../services/workflowValidationService';
import { useAuth } from './useAuth';

// Initial state
const initialState: WorkflowState = {
  currentWorkflow: null,
  savedWorkflows: [],
  isLoading: false,
  error: null,
  lastSaveStatus: 'idle'
};

// Workflow reducer
function workflowReducer(state: WorkflowState, action: WorkflowAction): WorkflowState {
  switch (action.type) {
    case 'CREATE_WORKFLOW':
      return {
        ...state,
        isLoading: false,
        error: null
      };

    case 'LOAD_WORKFLOW':
      return {
        ...state,
        isLoading: true,
        error: null
      };

    case 'LOAD_SAVED_WORKFLOWS':
      return {
        ...state,
        savedWorkflows: action.payload.workflows,
        isLoading: false,
        error: null
      };

    case 'UPDATE_STEP_DATA':
      if (!state.currentWorkflow) return state;
      
      const { stepId, data } = action.payload;
      
      // Check if this is the first time data is being added (workflow moves from not-started to in-progress)
      const hasDataBeforeUpdate = state.currentWorkflow.steps.some(step => 
        step.data && Object.keys(step.data).length > 0
      );
      const hasDataInCurrentUpdate = data && Object.keys(data).length > 0;
      
      // Helper function to determine step completion status
      const getStepCompletionStatus = (stepId: string, stepData: any): StepStatus => {
        if (!stepData || typeof stepData !== 'object') return 'pending';
        
        // Check if aide supervision is enabled from ANY step data or workflow metadata
        // Look for includeAideSupervision flag in any workflow step (it's stored in basic-info)
        const isSupervisionFormWorkflow = state.currentWorkflow?.type === 'supervision-form';
        const supervisionMetadata = isSupervisionFormWorkflow ? state.currentWorkflow?.metadata as any : null;
        
        // Check for includeAideSupervision in basic-info step or current step
        const basicInfoStep = (state.currentWorkflow?.steps as any[])?.find((step: any) => step.id === 'basic-info');
        const includeAideSupervision = stepData.includeAideSupervision ||
                                     basicInfoStep?.data?.includeAideSupervision ||
                                     supervisionMetadata?.supervisionType === 'BOTH' ||
                                     supervisionMetadata?.supervisionType === 'AIDE' ||
                                     supervisionMetadata?.employeeId;
        
        // Define base required fields for each step
        const baseStepRequirements: Record<string, string[]> = {
          'basic-info': ['supervisionDate', 'supervisionType', 'clientNdocNumber'],
          'section-1': ['aideAppropratelyAttired', 'introducesSlefToPatient', 'clearlyIdentifiesPatient', 'reliableAndPunctual', 'goodRelationshipWithPatient', 'ensuresPatientRights'],
          'section-2': ['usesStandardPrecautions', 'followsSpecificPrecautions'],
          'section-3': ['followsCarePlan', 'personalCareSkinBreakdown', 'dressing', 'mealPreparationFollowsDiet', 'transfers', 'exercises', 'catheterCare', 'ostomyCare', 'vitalSignsWeight', 'medicationReminding', 'lightHousekeeping', 'errandsShoppig', 'specialTasks'],
          'section-4': ['documentationNoAbbreviations', 'contactsCMChangesInCondition', 'contactsCMPatientComplaints', 'contactsCMPatientSafety'],
          'section-5': ['medicationNonCompliance', 'commentsRecommendationsPage1'],
          'page-2': ['involvesPatientInPlanning', 'allowsPatientControl', 'recognizesHearingSightLoss', 'slowsPaceForElderly', 'commentsRecommendationsPage2'],
          'validation': ['additionalValidationRequired', 'performanceAchieved'],
          'signatures': ['evaluatorSignature', 'patientSignature'], // Base signatures (employee signature added conditionally)
          'review': ['reviewCompleted', 'submissionReady']
        };
        
        // Get base requirements for this step
        let requiredFields = [...(baseStepRequirements[stepId] || [])];
        
        // Add conditional requirements based on aide supervision
        if (includeAideSupervision) {
          if (stepId === 'basic-info') {
            requiredFields.push('aideName'); // Only require aideName if aide supervision is enabled
          }
          if (stepId === 'signatures') {
            requiredFields.push('employeeSignature'); // Only require employeeSignature if aide supervision is enabled
          }
        }
        
        // If no required fields are defined for this step, consider it automatically completed
        if (requiredFields.length === 0) return 'completed';
        
        // Count filled fields (excluding default false values)
        const filledFields = requiredFields.filter(field => {
          const value = stepData[field];
          return value !== '' && value !== null && value !== undefined && value !== false;
        });
        
        if (filledFields.length === 0) return 'pending';
        if (filledFields.length === requiredFields.length) return 'completed';
        return 'current'; // Some but not all required fields are filled (partially completed)
      };
      
      const updatedSteps = state.currentWorkflow.steps.map(step => {
        if (step.id === stepId) {
          const updatedStepData = { ...step.data, ...data };
          const newStepStatus = getStepCompletionStatus(stepId, updatedStepData);
          
          return { 
            ...step, 
            data: updatedStepData,
            status: newStepStatus,
            validationErrors: [] // Clear errors when data is updated
          } as any;
        }
        return step;
      }) as any;

      // Check if all required steps are now completed
      const allRequiredStepsCompleted = updatedSteps
        .filter((step: any) => step.isRequired)
        .every((step: any) => step.status === 'completed');

      // Determine new workflow status
      let newWorkflowStatus = state.currentWorkflow.status;
      
      if (allRequiredStepsCompleted && state.currentWorkflow.status !== 'completed') {
        newWorkflowStatus = 'completed';
        console.log('🎉 Workflow automatically marked as completed - all required steps done!');
      } else if (!hasDataBeforeUpdate && hasDataInCurrentUpdate && state.currentWorkflow.status === 'not-started') {
        newWorkflowStatus = 'in-progress';
      }

      const updatedWorkflow = {
        ...state.currentWorkflow,
        steps: updatedSteps,
        lastSavedAt: new Date().toISOString(), // Update timestamp
        status: newWorkflowStatus,
        completedAt: newWorkflowStatus === 'completed' ? new Date().toISOString() : state.currentWorkflow.completedAt
      };
      
      // Immediately save to auto-save storage to prevent data loss
      try {
        localStorage.setItem('lighthouse_workflow_autosave', JSON.stringify(updatedWorkflow));
      } catch (error) {
        console.warn('Failed to immediately save workflow data:', error);
      }
      
      return {
        ...state,
        currentWorkflow: updatedWorkflow,
        lastSaveStatus: 'idle' // Reset save status to trigger auto-save
      };

    case 'COMPLETE_STEP':
      if (!state.currentWorkflow) return state;
      
      const { stepId: completedStepId } = action.payload;
      return {
        ...state,
        currentWorkflow: {
          ...state.currentWorkflow,
          steps: state.currentWorkflow.steps.map(step =>
            step.id === completedStepId
              ? { 
                  ...step, 
                  status: 'completed' as StepStatus,
                  completedAt: new Date().toISOString(),
                  validationErrors: []
                } as any
              : step
          ) as any
        }
      };

    case 'GO_TO_STEP':
      if (!state.currentWorkflow) return state;
      
      const { stepIndex } = action.payload;
      return {
        ...state,
        currentWorkflow: {
          ...state.currentWorkflow,
          currentStepIndex: stepIndex,
          steps: state.currentWorkflow.steps.map((step, index) => ({
            ...step,
            status: index === stepIndex ? 'current' as StepStatus : 
                   index < stepIndex ? 'completed' as StepStatus : 
                   'pending' as StepStatus
          })) as any
        }
      };

    case 'VALIDATE_STEP':
      if (!state.currentWorkflow) return state;
      
      const { stepId: validateStepId } = action.payload;
      const currentStepIndex = state.currentWorkflow.steps.findIndex(s => s.id === validateStepId);
      if (currentStepIndex === -1) return state;
      
      const validationErrors = workflowService.validateStep(state.currentWorkflow, currentStepIndex);
      
      return {
        ...state,
        currentWorkflow: {
          ...state.currentWorkflow,
          steps: state.currentWorkflow.steps.map(step =>
            step.id === validateStepId
              ? { ...step, validationErrors }
              : step
          ) as any
        }
      };

    case 'SAVE_WORKFLOW':
      return {
        ...state,
        lastSaveStatus: 'saving'
      };

    case 'SAVE_COMPLETE':
      return {
        ...state,
        lastSaveStatus: 'saved'
      };

    case 'RESET_SAVE_STATUS':
      return {
        ...state,
        lastSaveStatus: 'idle'
      };

    case 'PAUSE_WORKFLOW':
      if (!state.currentWorkflow) return state;
      
      return {
        ...state,
        currentWorkflow: {
          ...state.currentWorkflow,
          status: 'paused'
        }
      };

    case 'RESUME_WORKFLOW':
      if (!state.currentWorkflow) return state;
      
      return {
        ...state,
        currentWorkflow: {
          ...state.currentWorkflow,
          status: 'in-progress'
        }
      };

    case 'COMPLETE_WORKFLOW':
      if (!state.currentWorkflow) return state;
      
      return {
        ...state,
        currentWorkflow: {
          ...state.currentWorkflow,
          status: 'completed',
          completedAt: new Date().toISOString()
        }
      };

    case 'CANCEL_WORKFLOW':
      if (!state.currentWorkflow) return state;
      
      return {
        ...state,
        currentWorkflow: {
          ...state.currentWorkflow,
          status: 'cancelled'
        }
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload.error,
        isLoading: false,
        lastSaveStatus: 'error'
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };

    default:
      return state;
  }
}

// Create context
const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined);

// Hook to use workflow context
export const useWorkflow = () => {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
};

// Provider component props
interface WorkflowProviderProps {
  children: ReactNode;
}

// Workflow Provider Component
export const WorkflowProvider: React.FC<WorkflowProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(workflowReducer, initialState);
  const { user } = useAuth();

  // Load saved workflows on mount
  useEffect(() => {
    if (user) {
      const savedWorkflows = workflowService.getSavedWorkflows();
      dispatch({ type: 'LOAD_SAVED_WORKFLOWS', payload: { workflows: savedWorkflows || [] } });
    }
  }, [user]);

  // Auto-save current workflow when it changes
  useEffect(() => {
    if (state.currentWorkflow && state.lastSaveStatus === 'idle') {
      const timeoutId = setTimeout(() => {
        dispatch({ type: 'SAVE_WORKFLOW' });
        workflowService.autoSaveWorkflow(state.currentWorkflow!, () => {
          // Auto-save completed, update status to 'saved'
          dispatch({ type: 'SAVE_COMPLETE' });
        });
      }, 300); // Reduced from 1000ms to 300ms for faster auto-save

      return () => clearTimeout(timeoutId);
    }
  }, [state.currentWorkflow, state.lastSaveStatus]);

  // Reset save status back to 'idle' after showing 'saved' for a few seconds
  useEffect(() => {
    if (state.lastSaveStatus === 'saved') {
      const timeoutId = setTimeout(() => {
        dispatch({ type: 'RESET_SAVE_STATUS' });
      }, 3000); // Show 'saved' status for 3 seconds before resetting to 'idle'

      return () => clearTimeout(timeoutId);
    }
  }, [state.lastSaveStatus]);

  // Subscribe to workflow service changes
  useEffect(() => {
    const unsubscribe = workflowService.subscribe((workflows) => {
      dispatch({ type: 'LOAD_SAVED_WORKFLOWS', payload: { workflows: workflows || [] } });
    });

    return unsubscribe;
  }, []);

  // Workflow management functions
  const createWorkflow = useCallback(async (type: WorkflowType, metadata: any): Promise<string> => {
    try {
      if (!user) throw new Error('User must be authenticated to create workflows');
      
      const workflowMetadata = { ...metadata, userId: user.id };
      const newWorkflow = workflowService.createWorkflow(type, workflowMetadata);
      
      // Set as current workflow
      const newState = { ...state, currentWorkflow: newWorkflow, error: null };
      Object.assign(state, newState);
      
      // Save to storage
      workflowService.saveWorkflow(newWorkflow);
      dispatch({ type: 'CREATE_WORKFLOW', payload: { workflowType: type, metadata: workflowMetadata } });
      
      return newWorkflow.id;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create workflow';
      dispatch({ type: 'SET_ERROR', payload: { error: errorMessage } });
      throw error;
    }
  }, [user, state]);

  const loadWorkflow = useCallback(async (workflowId: string): Promise<void> => {
    try {
      dispatch({ type: 'LOAD_WORKFLOW', payload: { workflowId } });
      
      const workflow = workflowService.loadWorkflow(workflowId);
      if (!workflow) {
        throw new Error('Workflow not found');
      }
      
      const newState = { ...state, currentWorkflow: workflow, isLoading: false, error: null };
      Object.assign(state, newState);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load workflow';
      dispatch({ type: 'SET_ERROR', payload: { error: errorMessage } });
      throw error;
    }
  }, [state]);

  const saveWorkflow = useCallback(async (): Promise<void> => {
    if (!state.currentWorkflow) return;
    
    try {
      dispatch({ type: 'SAVE_WORKFLOW' });
      workflowService.saveWorkflow(state.currentWorkflow);
      dispatch({ type: 'SAVE_COMPLETE' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save workflow';
      dispatch({ type: 'SET_ERROR', payload: { error: errorMessage } });
    }
  }, [state.currentWorkflow]);

  const deleteWorkflow = useCallback(async (workflowId: string): Promise<void> => {
    try {
      const success = workflowService.deleteWorkflow(workflowId);
      if (!success) {
        throw new Error('Failed to delete workflow');
      }
      
      // If deleted workflow was current, clear it
      if (state.currentWorkflow?.id === workflowId) {
        const newState = { ...state, currentWorkflow: null };
        Object.assign(state, newState);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete workflow';
      dispatch({ type: 'SET_ERROR', payload: { error: errorMessage } });
      throw error;
    }
  }, [state.currentWorkflow]);

  // Step management functions
  const goToStep = useCallback((stepIndex: number) => {
    dispatch({ type: 'GO_TO_STEP', payload: { stepIndex } });
  }, []);

  const goToNextStep = useCallback(() => {
    if (!state.currentWorkflow) return;
    
    const nextIndex = state.currentWorkflow.currentStepIndex + 1;
    if (nextIndex < state.currentWorkflow.steps.length) {
      goToStep(nextIndex);
    }
  }, [state.currentWorkflow, goToStep]);

  const goToPreviousStep = useCallback(() => {
    if (!state.currentWorkflow) return;
    
    const prevIndex = state.currentWorkflow.currentStepIndex - 1;
    if (prevIndex >= 0) {
      goToStep(prevIndex);
    }
  }, [state.currentWorkflow, goToStep]);

  const updateStepData = useCallback((stepId: string, data: Record<string, any>) => {
    dispatch({ type: 'UPDATE_STEP_DATA', payload: { stepId, data } });
  }, []);

  const validateCurrentStep = useCallback((): boolean => {
    if (!state.currentWorkflow) return false;
    
    const currentStep = state.currentWorkflow.steps[state.currentWorkflow.currentStepIndex];
    if (!currentStep) return false;
    
    dispatch({ type: 'VALIDATE_STEP', payload: { stepId: currentStep.id } });
    
    const errors = workflowService.validateStep(state.currentWorkflow, state.currentWorkflow.currentStepIndex);
    return errors.length === 0;
  }, [state.currentWorkflow]);

  const completeCurrentStep = useCallback(() => {
    if (!state.currentWorkflow) return;
    
    const currentStep = state.currentWorkflow.steps[state.currentWorkflow.currentStepIndex];
    if (!currentStep) return;
    
    dispatch({ type: 'COMPLETE_STEP', payload: { stepId: currentStep.id } });
  }, [state.currentWorkflow]);

  // Workflow control functions
  const pauseWorkflow = useCallback(async (): Promise<void> => {
    dispatch({ type: 'PAUSE_WORKFLOW' });
    await saveWorkflow();
  }, [saveWorkflow]);

  const resumeWorkflow = useCallback(async (): Promise<void> => {
    dispatch({ type: 'RESUME_WORKFLOW' });
  }, []);

  const completeWorkflow = useCallback(async (): Promise<void> => {
    dispatch({ type: 'COMPLETE_WORKFLOW' });
    await saveWorkflow();
  }, [saveWorkflow]);

  const cancelWorkflow = useCallback(async (): Promise<void> => {
    dispatch({ type: 'CANCEL_WORKFLOW' });
    await saveWorkflow();
  }, [saveWorkflow]);

  // Utility functions
  const canGoToNextStep = useCallback((): boolean => {
    if (!state.currentWorkflow) return false;
    return state.currentWorkflow.currentStepIndex < state.currentWorkflow.steps.length - 1;
  }, [state.currentWorkflow]);

  const canGoToPreviousStep = useCallback((): boolean => {
    if (!state.currentWorkflow) return false;
    return state.currentWorkflow.currentStepIndex > 0;
  }, [state.currentWorkflow]);

  const getWorkflowProgress = useCallback(() => {
    if (!state.currentWorkflow) {
      return { completed: 0, total: 0, percentage: 0 };
    }
    return workflowService.getWorkflowProgress(state.currentWorkflow);
  }, [state.currentWorkflow]);

  const getSavedWorkflows = useCallback((type?: WorkflowType): AnyWorkflowInstance[] => {
    return type 
      ? workflowService.getWorkflowsByType(type)
      : workflowService.getSavedWorkflows();
  }, []);

  // Enhanced validation functions
  const getStepValidationResult = useCallback((stepIndex?: number): ValidationResult | null => {
    if (!state.currentWorkflow) return null;
    const index = stepIndex !== undefined ? stepIndex : state.currentWorkflow.currentStepIndex;
    return workflowService.getStepValidationResult(state.currentWorkflow, index);
  }, [state.currentWorkflow]);

  const validateEntireWorkflow = useCallback((): ValidationResult | null => {
    if (!state.currentWorkflow) return null;
    return workflowService.validateEntireWorkflow(state.currentWorkflow);
  }, [state.currentWorkflow]);

  const validateFieldRealTime = useCallback((
    fieldName: string, 
    value: any, 
    stepIndex?: number,
    allData?: any
  ): string | null => {
    if (!state.currentWorkflow) return null;
    
    const index = stepIndex !== undefined ? stepIndex : state.currentWorkflow.currentStepIndex;
    const step = state.currentWorkflow.steps[index];
    if (!step) return null;

    const stepType = (step as any).stepType;
    return workflowService.validateFieldRealTime(
      state.currentWorkflow.type, 
      stepType, 
      fieldName, 
      value, 
      allData
    );
  }, [state.currentWorkflow]);

  const getFieldValidationErrors = useCallback((stepIndex?: number): Record<string, string> => {
    const validationResult = getStepValidationResult(stepIndex);
    return validationResult ? validationResult.fieldErrors : {};
  }, [getStepValidationResult]);

  const hasValidationErrors = useCallback((stepIndex?: number): boolean => {
    const validationResult = getStepValidationResult(stepIndex);
    return validationResult ? !validationResult.isValid : false;
  }, [getStepValidationResult]);

  const getValidationWarnings = useCallback((stepIndex?: number): ValidationError[] => {
    const validationResult = getStepValidationResult(stepIndex);
    return validationResult ? validationResult.warnings : [];
  }, [getStepValidationResult]);

  const value: WorkflowContextType = {
    state,
    dispatch,
    createWorkflow,
    loadWorkflow,
    saveWorkflow,
    deleteWorkflow,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    updateStepData,
    validateCurrentStep,
    completeCurrentStep,
    pauseWorkflow,
    resumeWorkflow,
    completeWorkflow,
    cancelWorkflow,
    canGoToNextStep,
    canGoToPreviousStep,
    getWorkflowProgress,
    getSavedWorkflows,
    getStepValidationResult,
    validateEntireWorkflow,
    validateFieldRealTime,
    getFieldValidationErrors,
    hasValidationErrors,
    getValidationWarnings
  };

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
}; 